import { AlertCircle, Loader2, Pencil } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaForm } from "@/shared/components/forms/ColocaForm";
import { ColocaInputField } from "@/shared/components/forms/ColocaInputField";
import { ColocaPhoneField } from "@/shared/components/forms/ColocaPhoneField";
import { ColocaSelectField } from "@/shared/components/forms/ColocaSelectField";
import { useZodForm } from "@/shared/components/forms/useZodForm";
import { ColocaDrawer } from "@/shared/components/overlays/ColocaDrawer";
import { isOnboardingCountryIso3, withPreservedCountry } from "@/shared/data/onboarding-countries";
import { useCountryLabel } from "@/shared/hooks/useCountryLabel";
import useTranslations from "@/shared/lang/useTranslations";
import { HttpApiService } from "@/shared/services/httpApi.service";
import { colocaToast } from "@/shared/utils/toast";

import { COUNTRY_OPTIONS } from "../constants/clients.constants";
import { ClientsService } from "../services/clientsService";
import type { OrganizationDetailResponse, UpdateOrganizationBody } from "../types/clients-api.types";
import { getTypeBadgeVariant } from "../utils/clientsTableHelpers";
import { createEditOrganizationSchema, type EditOrganizationValues } from "../utils/editOrganizationSchema";
import { EditEntityHeader } from "./EditEntityHeader";
import { EditFormFooter } from "./EditFormFooter";

export interface EditOrganizationModalProps {
  isOpen: boolean;
  onClose: () => void;
  organizationId: string | null;
  onSuccess?: () => void;
}

export function EditOrganizationModal({
  isOpen,
  onClose,
  organizationId,
  onSuccess,
}: EditOrganizationModalProps) {
  const { t } = useTranslations("clients");
  const [organization, setOrganization] = useState<OrganizationDetailResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const loadOrganization = useCallback(async (id: string) => {
    setLoading(true);
    setLoadError(null);
    try {
      const { data } = await ClientsService.fetchOrganizationById(id);
      setOrganization(data);
    } catch (err) {
      setLoadError(HttpApiService.getErrorMessage(err));
      setOrganization(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen && organizationId) {
      loadOrganization(organizationId);
    } else if (!isOpen) {
      setOrganization(null);
      setLoadError(null);
    }
  }, [isOpen, organizationId, loadOrganization]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) onClose();
    },
    [onClose],
  );

  const handleSuccess = useCallback(() => {
    onSuccess?.();
    onClose();
  }, [onSuccess, onClose]);

  const typeBadgeLabel = organization?.type
    ? organization.type === "business"
      ? t("edit_field_type_business")
      : t("edit_field_type_individual")
    : "";

  return (
    <ColocaDrawer
      open={isOpen}
      onOpenChange={handleOpenChange}
      size="lg"
      title={
        <span className="flex items-center gap-2">
          <Pencil className="h-4 w-4 text-warning" aria-hidden />
          {t("edit_org_modal_title")}
        </span>
      }
      description={
        organization ? (
          <EditEntityHeader
            description={t("edit_org_modal_description")}
            typeBadgeLabel={typeBadgeLabel}
            typeBadgeVariant={getTypeBadgeVariant(organization.type ?? null)}
          />
        ) : (
          t("edit_org_modal_description")
        )
      }
    >
      {loading && (
        <div className="flex flex-col items-center justify-center py-10 gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <p className="text-sm text-muted-foreground">{t("edit_loading")}</p>
        </div>
      )}

      {loadError && !loading && <ColocaAlert variant="destructive" icon={AlertCircle} text={loadError} />}

      {organization && !loading && !loadError && (
        <EditOrganizationForm organization={organization} onSuccess={handleSuccess} onCancel={onClose} />
      )}
    </ColocaDrawer>
  );
}

// ─── Form ────────────────────────────────────────────────────────────

interface FormProps {
  organization: OrganizationDetailResponse;
  onSuccess: () => void;
  onCancel: () => void;
}

function EditOrganizationForm({ organization, onSuccess, onCancel }: FormProps) {
  const { t } = useTranslations("clients");
  const countryLabel = useCountryLabel();
  // Onboarding allowlist, plus the organization's stored country when it predates the gate (see
  // `withPreservedCountry`) — otherwise saving any other field would silently rewrite it.
  const countryOptions = useMemo(
    () =>
      withPreservedCountry(COUNTRY_OPTIONS, organization.country, isOnboardingCountryIso3).map(
        ({ label, value }) => ({ label: countryLabel(value, label), value }),
      ),
    [countryLabel, organization.country],
  );
  const schema = useMemo(() => createEditOrganizationSchema((key) => t(key) as string), [t]);

  const defaults: EditOrganizationValues = useMemo(
    () => ({
      name: organization.name ?? "",
      email: organization.email ?? "",
      phone: organization.phone ?? "",
      country: organization.country ?? "",
    }),
    [organization],
  );

  const form = useZodForm(schema, defaults);
  const {
    formState: { isSubmitting },
  } = form;

  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleSubmit = useCallback(
    async (values: EditOrganizationValues) => {
      setSubmitError(null);
      const diff = buildOrganizationDiff(organization, values);
      if (Object.keys(diff).length === 0) {
        colocaToast.info(t("edit_no_changes"));
        return;
      }
      try {
        await ClientsService.updateOrganization(organization.id, diff);
        colocaToast.success(t("edit_org_toast_success"));
        onSuccess();
      } catch (err) {
        const msg = HttpApiService.getErrorMessage(err) || t("edit_org_toast_error");
        setSubmitError(msg);
        colocaToast.error(t("edit_org_toast_error"));
      }
    },
    [organization, onSuccess, t],
  );

  return (
    <ColocaForm form={form} onSubmit={handleSubmit}>
      {submitError && <ColocaAlert variant="destructive" icon={AlertCircle} text={submitError} />}

      <ColocaInputField
        name="name"
        label={t("edit_org_label_name")}
        placeholder={t("edit_org_placeholder_name")}
      />

      <ColocaInputField
        name="email"
        label={t("modal_label_email")}
        placeholder={t("modal_placeholder_email")}
        type="email"
      />

      <ColocaPhoneField name="phone" label={t("modal_label_phone")} />

      <ColocaSelectField
        name="country"
        label={t("modal_label_country")}
        placeholder={t("modal_placeholder_country")}
        items={countryOptions}
      />

      <EditFormFooter isSubmitting={isSubmitting} onCancel={onCancel} />
    </ColocaForm>
  );
}

// ─── Diff builder ────────────────────────────────────────────────────

function normalize(value: string | null | undefined): string {
  return (value ?? "").trim();
}

function diffField<K extends keyof UpdateOrganizationBody>(
  patch: UpdateOrganizationBody,
  key: K,
  current: string | null | undefined,
  next: string | undefined,
): void {
  const nextNormalized = normalize(next);
  if (nextNormalized !== normalize(current)) {
    patch[key] = nextNormalized as UpdateOrganizationBody[K];
  }
}

function buildOrganizationDiff(
  organization: OrganizationDetailResponse,
  values: EditOrganizationValues,
): UpdateOrganizationBody {
  const patch: UpdateOrganizationBody = {};
  diffField(patch, "name", organization.name, values.name);
  diffField(patch, "email", organization.email, values.email);
  diffField(patch, "phone", organization.phone, values.phone);
  diffField(patch, "country", organization.country, values.country);
  return patch;
}
